'use client';

import { useEffect, useRef, useState } from 'react';
import Autoplay, { type AutoplayType } from 'embla-carousel-autoplay';

import {
  Carousel,
  CarouselContent,
  type CarouselApi,
} from '@components/ui/Carousel';
import FeedbackCarouselItem from '@components/homeSections/Feedback/FeedbackCarousel/FeedbackCarouselItem';

interface FeedbackCarouselProps {
  setApi: (api: CarouselApi) => void;
  setPlugin: (plugin: AutoplayType) => void;
  testimonials: {
    author: string;
    text: string;
  }[];
  isLastSlide: boolean;
}

export default function FeedbackCarousel({
  setApi,
  setPlugin,
  testimonials,
  isLastSlide,
}: FeedbackCarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);

  const [localApi, setLocalApi] = useState<CarouselApi>();

  const [isVisible, setIsVisible] = useState(false);

  const [plugin] = useState<AutoplayType>(() =>
    Autoplay({
      stopOnInteraction: true,
      playOnInit: true,
      delay: 3000,
    })
  );

  useEffect(() => {
    setPlugin(plugin);
  }, [plugin, setPlugin]);

  useEffect(() => {
    if (!carouselRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(carouselRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!localApi) return;

    const onSelect = () => {
      if (isLastSlide) {
        localApi.scrollTo(0);
      }
    };

    localApi.on('select', onSelect);

    return () => {
      localApi.off('select', onSelect);
    };
  }, [localApi, isLastSlide]);

  useEffect(() => {
    if (!localApi || !isVisible) {
      plugin.stop();
      return;
    }

    const startAutoplay = () => {
      if (localApi.internalEngine()) {
        plugin.play();
      }
    };

    const timeoutId = setTimeout(startAutoplay, 100);

    return () => {
      clearTimeout(timeoutId);
      plugin.stop();
    };
  }, [localApi, isVisible, plugin]);

  return (
    <div
      ref={carouselRef}
      className="w-full sm:w-full md:w-[422px] lg:w-[744px]"
    >
      <Carousel
        setApi={(api) => {
          setLocalApi(api);
          setApi(api);
        }}
        opts={{
          align: 'start',
          containScroll: false,
          dragFree: true,
          breakpoints: {
            '(min-width: 1024px)': {
              watchDrag: false,
            },
          },
        }}
        plugins={[plugin]}
      >
        <CarouselContent className="-ml-6">
          {testimonials.map((testimonial) => (
            <FeedbackCarouselItem
              key={testimonial.author}
              testimonial={testimonial}
            />
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
